import { useState, useCallback, useEffect } from "react";
import { useNotification } from "../../../context/NotificationContext";
import {
  fetchResourceLists,
  saveResourceList,
  deleteResourceList,
  updateResourceListOrder,
  type ResourceList,
  type ResourceListItem,
  type ResourceListItemRequest,
} from "../../../services/api/playground";
import type { ExtendedAssetInfo } from "./useAssetManager";

interface UseResourceListManagerParams {
  selectedAssets: string[];
  assetMetadata: Record<string, ExtendedAssetInfo>;
  onLoadResourceList: (assets: string[], metadata: Record<string, ExtendedAssetInfo>) => void;
}

export const useResourceListManager = ({
  selectedAssets,
  assetMetadata,
  onLoadResourceList,
}: UseResourceListManagerParams) => {
  const { showSuccess, showError } = useNotification();

  // State
  const [resourceLists, setResourceLists] = useState<ResourceList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeResourceListId, setActiveResourceListId] = useState<string | null>(null);

  // Load resource lists
  const loadResourceLists = useCallback(async () => {
    try {
      setIsLoading(true);
      const lists = await fetchResourceLists();
      setResourceLists(lists);
    } catch (error) {
      console.error("Error loading resource lists:", error);
      showError("Failed to load resource lists");
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Load resource lists on mount
  useEffect(() => {
    loadResourceLists();
  }, [loadResourceLists]);

  // Create a new resource list from current selection or custom resources
  const createResourceListFromSelection = useCallback(
    async (
      name: string, 
      description: string = "", 
      customAssets?: string[], 
      customMetadata?: Record<string, ExtendedAssetInfo>
    ) => {
      const assetsToUse = customAssets || selectedAssets;
      const metadataToUse = customMetadata || assetMetadata;

      if (assetsToUse.length === 0) {
        // Allow empty resource lists
        const newResourceList: ResourceList = {
          name,
          description,
          items: [],
        };

        try {
          const savedResourceList = await saveResourceList(newResourceList);
          setResourceLists((prev) => [...prev, savedResourceList]);
          showSuccess(`Resource list "${name}" created successfully`);
          return savedResourceList;
        } catch (error) {
          console.error("Error creating resource list:", error);
          showError("Failed to create resource list");
          return null;
        }
      }

      try {
        const items: ResourceListItem[] = assetsToUse.map((assetId, index) => {
          const metadata = metadataToUse[assetId];
          return {
            resource_type: metadata?.resourceType || "product",
            resource_id: metadata?.resourceId || assetId,
            stream_type: metadata?.streamType || "total_return",
            display_name: metadata?.displayName || assetId,
            position: index + 1,
          };
        });

        const newResourceList: ResourceList = {
          name,
          description,
          items,
        };

        const savedResourceList = await saveResourceList(newResourceList);
        setResourceLists((prev) => [...prev, savedResourceList]);
        showSuccess(`Resource list "${name}" created successfully`);
        return savedResourceList;
      } catch (error) {
        console.error("Error creating resource list:", error);
        showError("Failed to create resource list");
        return null;
      }
    },
    [selectedAssets, assetMetadata, showSuccess, showError]
  );

  // Load a resource list
  const loadResourceList = useCallback(
    (resourceListId: string) => {
      const resourceList = resourceLists.find((list) => list.id === resourceListId);
      if (!resourceList) {
        showError("Resource list not found");
        return;
      }

      // Convert resource list items back to asset format
      const assets = resourceList.items.map((item) => item.resource_id);
      const metadata: Record<string, ExtendedAssetInfo> = {};

      resourceList.items.forEach((item) => {
        metadata[item.resource_id] = {
          displayName: item.display_name,
          resourceId: item.resource_id,
          resourceType: item.resource_type,
          streamType: item.stream_type as any,
        };
      });

      onLoadResourceList(assets, metadata);
      setActiveResourceListId(resourceListId);
      
    },
    [resourceLists, onLoadResourceList, showSuccess, showError]
  );

  // Delete a resource list
  const deleteResourceListById = useCallback(
    async (resourceListId: string) => {
      const resourceList = resourceLists.find((list) => list.id === resourceListId);
      if (!resourceList) {
        showError("Resource list not found");
        return;
      }

      try {
        await deleteResourceList(resourceListId);
        setResourceLists((prev) => prev.filter((list) => list.id !== resourceListId));

        // If this was the active resource list, deselect it
        if (activeResourceListId === resourceListId) {
          setActiveResourceListId(null);
        }

        
      } catch (error) {
        console.error("Error deleting resource list:", error);
        showError("Failed to delete resource list");
      }
    },
    [resourceLists, activeResourceListId, showSuccess, showError]
  );

  // Edit a resource list (for now, just allow name changes)
  const editResourceList = useCallback(
    async (resourceListId: string, newName: string) => {
      const resourceList = resourceLists.find((list) => list.id === resourceListId);
      if (!resourceList) {
        showError("Resource list not found");
        return;
      }

      try {
        const updatedResourceList = {
          ...resourceList,
          name: newName,
        };

        const savedResourceList = await saveResourceList(updatedResourceList);
        setResourceLists((prev) =>
          prev.map((list) => (list.id === resourceListId ? savedResourceList : list))
        );
     
      } catch (error) {
        console.error("Error updating resource list:", error);
        showError("Failed to update resource list");
      }
    },
    [resourceLists, showSuccess, showError]
  );

  // Clear active resource list selection
  const clearActiveResourceList = useCallback(() => {
    setActiveResourceListId(null);
  }, []);

  // Update resource list order
  const updateResourceListOrderHandler = useCallback(
    async (orderedIds: string[]) => {
      // Update local state immediately for responsive UI
      const newOrder = orderedIds.map((id) =>
        resourceLists.find((list) => list.id === id)
      ).filter(Boolean) as ResourceList[];
      
      // Update parent state immediately
      setResourceLists(newOrder);
      
      // Try to persist to backend (but don't revert on failure)
      try {
        console.log("Calling updateResourceListOrder with:", orderedIds);
        const success = await updateResourceListOrder(orderedIds);
        if (success) {
          showSuccess("Resource list order updated");
        } else {
          console.warn("Backend order update failed, but order persists locally");
          // Don't show error to user since the order is still working locally
        }
      } catch (error) {
        console.error("Error updating resource list order:", error);
        console.warn("Backend order update failed, but order persists locally");
        // Don't show error to user since the order is still working locally
      }
    },
    [resourceLists, showSuccess, showError]
  );

  // Update resource list icon
  const updateResourceListIcon = useCallback(
    async (resourceListId: string, iconName: string | undefined) => {
      const resourceList = resourceLists.find((list) => list.id === resourceListId);
      if (!resourceList) {
        showError("Resource list not found");
        return;
      }

      try {
        const updatedResourceList = {
          ...resourceList,
          icon: iconName,
        };

        const savedResourceList = await saveResourceList(updatedResourceList);
        setResourceLists((prev) =>
          prev.map((list) => (list.id === resourceListId ? savedResourceList : list))
        );
       
      } catch (error) {
        console.error("Error updating resource list icon:", error);
        showError("Failed to update resource list icon");
      }
    },
    [resourceLists, showSuccess, showError]
  );

  // Update resource list color
  const updateResourceListColor = useCallback(
    async (resourceListId: string, colorHex: string | undefined) => {
      const resourceList = resourceLists.find((list) => list.id === resourceListId);
      if (!resourceList) {
        showError("Resource list not found");
        return;
      }

      try {
        const updatedResourceList = {
          ...resourceList,
          color: colorHex,
        };

        const savedResourceList = await saveResourceList(updatedResourceList);
        setResourceLists((prev) =>
          prev.map((list) => (list.id === resourceListId ? savedResourceList : list))
        );
     
      } catch (error) {
        console.error("Error updating resource list color:", error);
        showError("Failed to update resource list color");
      }
    },
    [resourceLists, showSuccess, showError]
  );

  // Toggle favourite status for a resource list
  const toggleFavourite = useCallback(
    async (resourceListId: string, isFavourite: boolean) => {
      const resourceList = resourceLists.find((list) => list.id === resourceListId);
      if (!resourceList) {
        showError("Resource list not found");
        return;
      }

      // Optimistic UI update
      setResourceLists((prev) =>
        prev.map((list) =>
          list.id === resourceListId ? { ...list, is_favourite: isFavourite } : list
        )
      );

      try {
        const updatedResourceList = {
          ...resourceList,
          is_favourite: isFavourite,
        };

        const savedResourceList = await saveResourceList(updatedResourceList);
        // Update with server response to ensure consistency
        setResourceLists((prev) =>
          prev.map((list) => (list.id === resourceListId ? savedResourceList : list))
        );
      } catch (error) {
        console.error("Error toggling favourite status:", error);
        // Revert optimistic update on error
        setResourceLists((prev) =>
          prev.map((list) =>
            list.id === resourceListId ? { ...list, is_favourite: !isFavourite } : list
          )
        );
        showError("Failed to update favourite status");
      }
    },
    [resourceLists, showError]
  );

  // Update a resource list and update local state
  const updateResourceList = useCallback(
    async (updatedResourceList: ResourceList) => {
      const saved = await saveResourceList(updatedResourceList);
      setResourceLists((prev) =>
        prev.map((list) => (list.id === saved.id ? saved : list))
      );
      return saved;
    },
    []
  );

  // Set active resource list ID directly
  const setActiveResourceListIdDirectly = useCallback(
    (resourceListId: string | null) => {
      setActiveResourceListId(resourceListId);
    },
    []
  );

  return {
    resourceLists,
    isLoading,
    activeResourceListId,
    loadResourceLists,
    createResourceListFromSelection,
    loadResourceList,
    deleteResourceListById,
    editResourceList,
    clearActiveResourceList,
    updateResourceListOrderHandler,
    updateResourceListIcon,
    updateResourceListColor,
    toggleFavourite,
    updateResourceList,
    setActiveResourceListIdDirectly,
  };
};